import { apiFetch } from '../api/client';
import { monthOf } from '../audit/analytics';
import { addMonths, easternMonthOf, monthKey, parseDistrictSummary, type DistrictSummary } from '../audit/districtSummary';
import { summaryAudits } from '../audit/districtSummaryRead';
import { getListItems, odataString, type SpListItem } from '../graph/lists';
import { invalidateList } from '../graph/readCache';
import { getSubmittedAudits, recentAuditsSince, type AuditRecord } from './audits';
import { LISTS, READ_CACHE_MS } from './listNames';
import type { Store } from './types';

/**
 * Reading the monthly district summaries (owner ruling 2026-10-22; builder
 * in src/audit/districtSummary.ts, written by the server on each audit
 * credit and by Rebuild). The server returns only the districts the viewer
 * sees in FULL (every store), and /api/me says which ones those are
 * (`summaryDistricts`) — so a page uses summaries only when they cover every
 * store it shows (summariesCover), else it reads audits as before.
 */

/** '*' = every district; else trimmed + lower-case names. null = not known (direct mode / older server). */
export type SummaryDistricts = '*' | string[] | null;

interface SummaryFields {
  District: string;
  MonthStart: string;
  Summary: string;
  Stale: boolean | null;
  RebuiltAt: string | null;
}

export interface SummaryRow {
  /** Trimmed + lower-case district. */
  district: string;
  /** The district as written on the row. */
  districtName: string;
  summary: DistrictSummary;
}

export interface SummaryRead {
  rows: SummaryRow[];
  /** District-months marked stale (Remove-Audit.ps1, a failed rebuild) — the page asks the server to rebuild them. */
  stale: { district: string; month: string }[];
}

const norm = (s: string) => s.trim().toLowerCase();

/** One row per district + month (two rows can exist after a create race — the newest rebuild wins). Pure. */
export function pickSummaryRows(items: readonly Pick<SpListItem<Partial<SummaryFields>>, 'id' | 'fields'>[]): SummaryRead {
  const best = new Map<string, { at: string; row: SummaryRow | null; stale: boolean; month: string; districtName: string }>();
  for (const item of items) {
    const f = item.fields;
    const districtName = typeof f.District === 'string' ? f.District.trim() : '';
    if (!districtName) continue;
    const summary = parseDistrictSummary(f.Summary);
    const month = summary?.month ?? (typeof f.MonthStart === 'string' ? f.MonthStart.slice(0, 7) : '');
    if (!/^\d{4}-\d{2}$/.test(month)) continue;
    const key = `${norm(districtName)}\u0000${month}`;
    const at = typeof f.RebuiltAt === 'string' ? f.RebuiltAt : '';
    const prev = best.get(key);
    if (prev && prev.at >= at) continue;
    best.set(key, {
      at,
      month,
      districtName,
      stale: f.Stale === true,
      row: summary && summary.month === month ? { district: norm(districtName), districtName, summary } : null,
    });
  }
  const rows: SummaryRow[] = [];
  const stale: SummaryRead['stale'] = [];
  for (const v of best.values()) {
    if (v.row) rows.push(v.row);
    // An unreadable summary is as good as stale: the server rebuilds it from the audits.
    if (v.stale || !v.row) stale.push({ district: v.districtName, month: v.month });
  }
  rows.sort((a, b) => a.summary.month.localeCompare(b.summary.month) || a.district.localeCompare(b.district));
  return { rows, stale };
}

/** Summaries from `fromMonth` ('YYYY-MM'; '' = all) on. */
export async function getDistrictSummaries(fromMonth: string): Promise<SummaryRead> {
  const items = await getListItems<Partial<SummaryFields>>(LISTS.districtSummaries, {
    ...(fromMonth ? { filter: `fields/MonthStart ge ${odataString(monthKey(fromMonth))}` } : {}),
    cacheMs: READ_CACHE_MS,
  });
  return pickSummaryRows(items);
}

/** Ask the server to rebuild one month (all districts for an Admin; one stale district for anyone who sees it). */
export function rebuildSummaries(month: string, district?: string): Promise<{ rebuilt: number; audits: number }> {
  return apiFetch('/summaries/rebuild', { method: 'POST', body: district ? { month, district } : { month }, write: true });
}

/** At most this many stale district-months are rebuilt per page load. */
export const MAX_SELF_HEAL = 6;

/**
 * The stale district-months a page rebuilds: only districts it shows
 * (`districts` trimmed + lower-case; null = any), at most `max`. Pure.
 */
export function staleToHeal(
  stale: readonly { district: string; month: string }[],
  districts: ReadonlySet<string> | null,
  max: number = MAX_SELF_HEAL,
  now: Date = new Date(),
): { district: string; month: string }[] {
  // Newest first, and only months a page may refresh (the server refuses older
  // ones for non-admins — QA 2026-10-22: refused months used up the slots).
  const oldest = addMonths(easternMonthOf(now.toISOString()), -SELF_HEAL_MONTHS);
  return stale
    .filter((s) => (districts === null || districts.has(norm(s.district))) && s.month >= oldest)
    .sort((x, y) => y.month.localeCompare(x.month))
    .slice(0, max);
}

/** How far back a page may refresh (the server's SELF_HEAL_MONTHS — api/src/summaries.ts). */
export const SELF_HEAL_MONTHS = 14;

/**
 * District-months being rebuilt right now, by any page of this tab: a later
 * caller WAITS for the same rebuild instead of skipping it (QA 2026-10-22: a
 * re-run page kept the "refreshing" note when it skipped and the first run's
 * result was dropped). Resolves true when it rebuilt.
 */
const healing = new Map<string, Promise<boolean>>();
const healKey = (s: { district: string; month: string }) => `${norm(s.district)}\u0000${s.month}`;

export interface SummaryView {
  rows: SummaryRow[];
  /** Stale months in the page's districts are still waiting (the amber "being refreshed" note). */
  refreshing: boolean;
}

/**
 * Summaries from `fromMonth` on, returned AS READ — rendering never waits
 * for a rebuild (QA 2026-10-22). Stale months in the page's districts are
 * rebuilt in the BACKGROUND (at most MAX_SELF_HEAL, one by one, errors
 * ignored), and `onHealed` gets the re-read once something was rebuilt.
 * The caller guards `onHealed` against an unmounted or re-run page.
 */
export async function readSummaries(
  fromMonth: string,
  opts: { districts?: ReadonlySet<string> | null; onHealed?: (view: SummaryView) => void } = {},
): Promise<SummaryView> {
  const districts = opts.districts ?? null;
  const first = await getDistrictSummaries(fromMonth);
  const pending = staleToHeal(first.stale, districts, Number.POSITIVE_INFINITY);
  const todo = staleToHeal(first.stale, districts);
  if (todo.length > 0 && opts.onHealed) {
    const onHealed = opts.onHealed;
    void (async () => {
      let rebuilt = 0;
      for (const s of todo) {
        const key = healKey(s);
        let job = healing.get(key);
        if (!job) {
          job = rebuildSummaries(s.month, s.district).then(
            () => true,
            // not allowed / busy — the note stays, the next load tries again
            () => false,
          );
          healing.set(key, job);
          void job.finally(() => healing.delete(key));
        }
        if (await job) rebuilt += 1;
      }
      if (rebuilt === 0) return;
      // The rebuild wrote rows the 1-minute read cache doesn't know about.
      invalidateList(LISTS.districtSummaries);
      try {
        const again = await getDistrictSummaries(fromMonth);
        onHealed({ rows: again.rows, refreshing: staleToHeal(again.stale, districts, Number.POSITIVE_INFINITY).length > 0 });
      } catch {
        // keep what's on screen
      }
    })();
  }
  return { rows: first.rows, refreshing: pending.length > 0 };
}

/** The last `n` Eastern months, newest first ('YYYY-MM'). Pure. */
export function recentMonths(n: number, now: Date = new Date()): string[] {
  const top = easternMonthOf(now.toISOString());
  return Array.from({ length: Math.max(0, n) }, (_, k) => addMonths(top, -k));
}

/**
 * Admin: rebuild every district's summary for these months, one month per
 * server call, in the order given. Stops at the first failure (thrown).
 */
export async function rebuildMonthList(
  months: readonly string[],
  onProgress?: (done: number, total: number, month: string) => void,
): Promise<{ audits: number }> {
  let audits = 0;
  try {
    for (const [k, month] of months.entries()) {
      onProgress?.(k, months.length, month);
      audits += (await rebuildSummaries(month)).audits;
    }
    onProgress?.(months.length, months.length, '');
  } finally {
    invalidateList(LISTS.districtSummaries);
  }
  return { audits };
}

/** Admin: the last `n` months (13 = the dashboards' window) — after a store move or district rename. */
export function rebuildRecentMonths(
  n: number,
  onProgress?: (done: number, total: number, month: string) => void,
): Promise<{ audits: number }> {
  return rebuildMonthList(recentMonths(n), onProgress);
}

/** The Eastern months a set of audits falls in, newest first, each once. Pure. */
export function monthsOf(audits: readonly { submittedAt: string }[]): string[] {
  return [...new Set(audits.map((a) => easternMonthOf(a.submittedAt)).filter(Boolean))].sort().reverse();
}

/**
 * Can a page decide about summaries without its store list? true/false
 * when it can; null when it needs the stores (a named district list).
 * Pure — "no summaries" and "all districts" start reading at once instead
 * of waiting for the store list (QA 2026-10-22).
 */
export function coverWithoutStores(districts: SummaryDistricts | undefined, impersonating: boolean): boolean | null {
  if (impersonating || districts == null) return false;
  if (districts === '*') return true;
  return districts.length === 0 ? false : null;
}

/**
 * The "N audits not summarized" note in summaries mode: the stored count
 * covers whole districts (inactive / moved stores too), so it's clamped to
 * the audits the view shows. Pure.
 */
export function summarizedCounts(shown: number, unsummarized: number): { missing: number; summarized: number } {
  const missing = Math.max(0, Math.min(shown, unsummarized));
  return { missing, summarized: Math.max(0, shown - missing) };
}

/**
 * Do the summaries cover everything this page shows? Only when the server
 * named the districts (never while "View as" narrows the screen — the rows
 * would still be the real account's) and every visible active store's
 * district is among them. Pure.
 */
export function summariesCover(
  districts: SummaryDistricts,
  visibleStores: readonly Pick<Store, 'district' | 'active'>[],
  impersonating: boolean,
): boolean {
  if (impersonating || districts === null) return false;
  if (districts === '*') return true;
  if (districts.length === 0) return false;
  const full = new Set(districts.map(norm));
  const active = visibleStores.filter((s) => s.active);
  return active.length > 0 && active.every((s) => full.has(norm(s.district)));
}

/**
 * The submitted audits Home and district pages show (the last ~13 months):
 * from the summaries when they cover the viewer, else the audits read as
 * before — and on any summary error, the audits read too (never a broken
 * page). `districts` = the districts the page shows (null = all): only
 * their stale months are rebuilt, in the background; `onUpdate` gets the
 * list again once something was rebuilt.
 */
export async function getRecentAudits(
  cover: boolean,
  opts: {
    districts?: ReadonlySet<string> | null;
    onUpdate?: (r: { audits: AuditRecord[]; refreshing: boolean }) => void;
  } = {},
): Promise<{ audits: AuditRecord[]; refreshing: boolean }> {
  const since = recentAuditsSince();
  const recent = (rows: readonly SummaryRow[]) =>
    summaryAudits(rows.map((r) => r.summary)).filter((a) => a.submittedAt >= since);
  if (cover) {
    try {
      // From one month before the window's local month: rows are Eastern months.
      const onUpdate = opts.onUpdate;
      const view = await readSummaries(addMonths(monthOf(since), -1), {
        districts: opts.districts ?? null,
        ...(onUpdate ? { onHealed: (v: SummaryView) => onUpdate({ audits: recent(v.rows), refreshing: v.refreshing }) } : {}),
      });
      return { audits: recent(view.rows), refreshing: view.refreshing };
    } catch (err) {
      console.warn('[AuditPlus] monthly summaries unavailable — reading audits instead', err);
    }
  }
  return { audits: await getSubmittedAudits({ since, slim: true }), refreshing: false };
}
